import { useRef, useCallback, useEffect } from 'react';

export interface SwipeDirection {
  direction: 'left' | 'right' | 'up' | 'down';
  distance: number;
  velocity: number;
}

export interface GestureConfig {
  swipe?: {
    threshold?: number;
    velocityThreshold?: number;
    onSwipe?: (direction: SwipeDirection) => void;
  };
  longPress?: {
    duration?: number;
    onLongPress?: (event: TouchEvent | MouseEvent) => void;
  };
  drag?: {
    onDragStart?: (event: TouchEvent | MouseEvent) => void;
    onDragMove?: (event: TouchEvent | MouseEvent, delta: { x: number; y: number }) => void;
    onDragEnd?: (event: TouchEvent | MouseEvent) => void;
  };
}

export interface GestureState {
  isPressed: boolean;
  isDragging: boolean;
  startPosition: ({ x: number; y: number; timestamp?: number }) | null;
  currentPosition: { x: number; y: number } | null;
}

export function useGestures<T extends HTMLElement = HTMLDivElement>(config: GestureConfig = {}) {
  const elementRef = useRef<T>(null);
  const gestureStateRef = useRef<GestureState>({
    isPressed: false,
    isDragging: false,
    startPosition: null,
    currentPosition: null,
  });
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);

  const {
    swipe = {},
    longPress = {},
    drag = {}
  } = config;

  const {
    threshold: swipeThreshold = 50,
    velocityThreshold = 0.3,
    onSwipe
  } = swipe;

  const {
    duration: longPressDuration = 500,
    onLongPress
  } = longPress;

  const {
    onDragStart,
    onDragMove,
    onDragEnd
  } = drag;

  // Get position from touch or mouse event
  const getEventPosition = useCallback((event: TouchEvent | MouseEvent) => {
    if ('touches' in event && event.touches.length > 0) {
      return { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }
    return { x: (event as MouseEvent).clientX, y: (event as MouseEvent).clientY };
  }, []);

  // Calculate swipe direction and properties
  const calculateSwipe = useCallback((start: { x: number; y: number }, end: { x: number; y: number }, duration: number) => {
    const deltaX = end.x - start.x;
    const deltaY = end.y - start.y;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const velocity = distance / duration;

    if (distance < swipeThreshold || velocity < velocityThreshold) {
      return null;
    }

    let direction: 'left' | 'right' | 'up' | 'down';
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      direction = deltaX > 0 ? 'right' : 'left';
    } else {
      direction = deltaY > 0 ? 'down' : 'up';
    }

    return { direction, distance, velocity };
  }, [swipeThreshold, velocityThreshold]);

  // Handle start events (touch/mouse down)
  const handleStart = useCallback((event: TouchEvent | MouseEvent) => {
    const position = getEventPosition(event);
    const state = gestureStateRef.current;
    
    state.isPressed = true;
    state.startPosition = position;
    state.currentPosition = position;
    state.isDragging = false;

    // Start long press timer
    if (onLongPress) {
      longPressTimerRef.current = setTimeout(() => {
        if (state.isPressed && !state.isDragging) {
          onLongPress(event);
        }
      }, longPressDuration);
    }

    // Trigger drag start
    if (onDragStart) {
      onDragStart(event);
    }
  }, [getEventPosition, onLongPress, onDragStart, longPressDuration]);

  // Handle move events (touch/mouse move)
  const handleMove = useCallback((event: TouchEvent | MouseEvent) => {
    const state = gestureStateRef.current;
    
    if (!state.isPressed || !state.startPosition) {return;}

    const position = getEventPosition(event);
    state.currentPosition = position;

    const deltaX = position.x - state.startPosition.x;
    const deltaY = position.y - state.startPosition.y;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const hasDragHandlers = !!(onDragStart ?? onDragMove ?? onDragEnd);

    // If moved enough, cancel long press; only mark as dragging when drag
    // handlers are configured — otherwise a real swipe (>10px) would set
    // isDragging and suppress the onSwipe check in handleEnd.
    if (distance > 10) {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
      if (hasDragHandlers && !state.isDragging) {
        state.isDragging = true;
      }
    }

    // Trigger drag move
    if (state.isDragging && onDragMove) {
      onDragMove(event, { x: deltaX, y: deltaY });
    }
  }, [getEventPosition, onDragMove, onDragStart, onDragEnd]);

  // Handle end events (touch/mouse up)
  const handleEnd = useCallback((event: TouchEvent | MouseEvent) => {
    const state = gestureStateRef.current;
    
    if (!state.isPressed || !state.startPosition || !state.currentPosition) {return;}

    // Clear long press timer
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    // Calculate swipe if not dragging
    if (!state.isDragging && onSwipe) {
      const duration = Date.now() - (state.startPosition?.timestamp ?? Date.now() - 100);
      const swipeResult = calculateSwipe(state.startPosition!, state.currentPosition!, duration);
      if (swipeResult) {
        onSwipe(swipeResult);
      }
    }

    // Trigger drag end
    if (state.isDragging && onDragEnd) {
      onDragEnd(event);
    }

    // Reset state
    state.isPressed = false;
    state.isDragging = false;
    state.startPosition = null;
    state.currentPosition = null;
  }, [calculateSwipe, onSwipe, onDragEnd]);

  // Add timestamp to start position for swipe velocity calculation
  const enhancedHandleStart = useCallback((event: TouchEvent | MouseEvent) => {
    handleStart(event);
    if (gestureStateRef.current.startPosition) {
      gestureStateRef.current.startPosition.timestamp = Date.now();
    }
  }, [handleStart]);

  // Set up event listeners
  useEffect(() => {
    const element = elementRef.current;
    if (!element) {return;}

    // Touch events
    element.addEventListener('touchstart', enhancedHandleStart, { passive: false });
    element.addEventListener('touchmove', handleMove, { passive: false });
    element.addEventListener('touchend', handleEnd, { passive: false });
    element.addEventListener('touchcancel', handleEnd, { passive: false });

    // Mouse events (for desktop testing)
    element.addEventListener('mousedown', enhancedHandleStart);
    element.addEventListener('mousemove', handleMove);
    element.addEventListener('mouseup', handleEnd);
    element.addEventListener('mouseleave', handleEnd);

    return () => {
      element.removeEventListener('touchstart', enhancedHandleStart);
      element.removeEventListener('touchmove', handleMove);
      element.removeEventListener('touchend', handleEnd);
      element.removeEventListener('touchcancel', handleEnd);
      element.removeEventListener('mousedown', enhancedHandleStart);
      element.removeEventListener('mousemove', handleMove);
      element.removeEventListener('mouseup', handleEnd);
      element.removeEventListener('mouseleave', handleEnd);
    };
  }, [enhancedHandleStart, handleMove, handleEnd]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  return {
    ref: elementRef,
    gestureState: gestureStateRef.current,
  };
}

// Convenience hooks for specific gestures
export function useSwipe<T extends HTMLElement = HTMLDivElement>(onSwipe: (direction: SwipeDirection) => void, options?: Partial<GestureConfig['swipe']>) {
  return useGestures<T>({
    swipe: {
      ...options,
      onSwipe,
    },
  });
}

export function useLongPress(onLongPress: (event: TouchEvent | MouseEvent) => void, duration = 500) {
  return useGestures({
    longPress: {
      duration,
      onLongPress,
    },
  });
}

export function useDrag(handlers: GestureConfig['drag']) {
  return useGestures({
    drag: handlers,
  });
}
